import type { ReactNode } from 'react';

import { cx } from './cx.js';
import { Icon } from './icons.js';
import styles from './states.module.css';

/** Contenedor estándar de contenido. */
export function Card({ children }: Readonly<{ readonly children: ReactNode }>) {
  return <section className={styles.card}>{children}</section>;
}

/**
 * Esqueleto de carga.
 *
 * Reservar la forma del contenido se percibe más rápido que un spinner, porque
 * el usuario ve de inmediato qué va a aparecer y la página no salta al llegar
 * los datos.
 */
export function Skeleton({ rows = 5 }: Readonly<{ readonly rows?: number }>) {
  return (
    <div aria-busy="true" aria-live="polite" className={styles.skeletonWrap} role="status">
      <span className="sr-only">Cargando contenido...</span>
      {Array.from({ length: rows }, (_, index) => (
        <span className={styles.skeleton} key={index} />
      ))}
    </div>
  );
}

/** Estado de carga puntual. `role="status"` lo anuncia sin interrumpir. */
export function LoadingState({ label = 'Cargando...' }: Readonly<{ readonly label?: string }>) {
  return (
    <div className={styles.centered} role="status">
      <div className={styles.spinner} />
      <p className={styles.message}>{label}</p>
    </div>
  );
}

/** Estado vacío: explica qué falta y, si se puede, cómo resolverlo. */
export function EmptyState({
  title,
  message,
  action,
}: Readonly<{
  readonly title: string;
  readonly message?: string;
  readonly action?: ReactNode;
}>) {
  return (
    <div className={styles.centered}>
      <span aria-hidden="true" className={styles.badge}>
        <Icon height={22} name="caja" width={22} />
      </span>
      <p className={styles.title}>{title}</p>
      {message === undefined ? null : <p className={styles.message}>{message}</p>}
      {action}
    </div>
  );
}

/** Estado de error, con reintento cuando la operación lo admite. */
export function ErrorState({
  title = 'No se pudo cargar',
  message,
  action,
}: Readonly<{
  readonly title?: string;
  readonly message: string;
  readonly action?: ReactNode;
}>) {
  return (
    <div className={styles.centered} role="alert">
      <span aria-hidden="true" className={cx(styles.badge, styles.badgeDanger)}>
        <Icon height={22} name="alertas" width={22} />
      </span>
      <p className={styles.title}>{title}</p>
      <p className={styles.message}>{message}</p>
      {action}
    </div>
  );
}
